import React, { useCallback, useEffect, useRef, useState } from "react";
import { getSameVariants } from "utils/api";
import { useDispatch, useSelector } from "react-redux";
import { getMerchantConfig, getRmsConfig } from "features/config/configSlice";
import {
  addNewProduct,
  addProceedBool,
  addProductType,
  addUploads,
  getSelectedGroupProducts,
  getSelectedProduct,
  getSelectedReason,
  getSelectedSubReason,
  mobSelected,
  returnQuantity,
  returnType,
  getReplacementMode,
  setReplacementMode,
} from "features/product/productSlice";
import {
  addReturnedProducts,
  getReturnedProducts,
} from "features/returnedProducts/returnedProductSlice";
import {
  Button,
  MenuItem,
  Modal,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import {
  getSelectedOrder,
  updateWebSelected,
} from "features/orders/orderSlice";
// import { ReactComponent as FrownIcon } from "assets/frown.svg";
import { StyledDifferentProduct, StyledVariantSelect } from "./styled";
import { ReactComponent as Search } from "assets/search.svg";
import SizeSelector from "./SizeSelector";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { LazyLoadImage } from "react-lazy-load-image-component";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import { getLineItemMetadata } from "utils/lineItemMetadata";
import parse from "html-react-parser";
import LoadingError from "components/v1/common/LoadingError";
import { ReactComponent as CloseIcon } from "assets/close.svg";

function SameProduct({
  quantityInc,
  productType,
  selectedVariant,
  setSelectedVariant,
  otherReason,
  setAnimate,
  setUploads,
}) {
  const [sameVariantProducts, setSameVariantProducts] = useState({});
  const [sameVariantLoader, setSameVariantLoader] = useState(true);
  const [sameApiError, setSameApiError] = useState(false);
  const [APIVersion, setAPIVersion] = useState("v1");
  const [selectedCategories, setSelectedCategories] = useState({});
  const [customProperties, setCustomProperties] = useState([]);
  const [customSizeError, setCustomSizeError] = useState(false);
  const [customProductGroupId, setCustomProductGroupId] = useState("");
  const [selectedFilterVariantId, setSelectedFilterVariantId] = useState(null);
  const [sameVariantConfirmationOpen, setSameVariantConfirmationOpen] =
    useState(false);
  const hasAutoProcessedRef = useRef(false);

  const merchantConfig = useSelector(getMerchantConfig);
  const rmsConfig = useSelector(getRmsConfig);
  const selectedProduct = useSelector(getSelectedProduct);
  const returnedProducts = useSelector(getReturnedProducts);
  const reason = useSelector(getSelectedReason);
  const subReason = useSelector(getSelectedSubReason);
  const selectedOrder = useSelector(getSelectedOrder);
  const selectedGroupProducts = useSelector(getSelectedGroupProducts);
  const isReplacement = useSelector(getReplacementMode);

  const dispatch = useDispatch();

  const isOriginalVariant = useCallback(
    (variant) =>
      variant &&
      selectedProduct &&
      String(variant.variant_id) === String(selectedProduct.variant_id),
    [selectedProduct],
  );

  const isSameVariantSelected = isOriginalVariant(selectedVariant);

  const openSameVariantConfirmationIfNeeded = useCallback((variant) => {
    if (isOriginalVariant(variant)) {
      setSameVariantConfirmationOpen(true);
    }
  }, [isOriginalVariant]);

  const isSelectedVariantUnavailable =
    selectedVariant &&
    selectedVariant !== null &&
    !isSameVariantSelected &&
    (selectedVariant.out_of_stock ||
      !selectedVariant.quantity_available_for_exchange ||
      (merchantConfig.merchant_id === 541 &&
        selectedVariant.inventory_quantity < 7));

  const buildVariantFromProduct = (product) => {
    if (!product) {
      return null;
    }

    return {
      variant_id: product.variant_id,
      sku: product.sku,
      price: product.product_price,
      title: product.variant_name || product.product_title,
      variant_img: product.image_url ? { src: product.image_url } : undefined,
      original_product_price: product.original_product_price,
      variant_weight: product?.variant_weight || "",
      variant_weight_unit: product?.variant_weight_unit || "",
    };
  };

  const orderRequiredProduct = (product, version) => {
    if (version === "v1") {
      setSameVariantProducts(product);
    } else {
      const unorderedProduct = { ...product };

      // Sort the attributes
      unorderedProduct.attributes = unorderedProduct.attributes.sort((a, b) => {
        return a.position - b.position;
      });

      // Sort the 'values' array inside each attribute and remove values that
      // do not exist in any variant. If an attribute itself has no matching
      // variants, drop the attribute entirely.

      // Map of attribute id to the set of value ids present in variants
      const availableValuesMap = {};
      unorderedProduct.variants.forEach((variant) => {
        variant.attributes.forEach((va) => {
          if (!availableValuesMap[va.product_attribute]) {
            availableValuesMap[va.product_attribute] = new Set();
          }
          availableValuesMap[va.product_attribute].add(va.value);
        });
      });

      unorderedProduct.attributes = unorderedProduct.attributes
        .map((attribute) => {
          if (!availableValuesMap[attribute.id]) {
            return null; // attribute not present in any variant
          }

          const validValues = (attribute.values || [])
            .filter((val) => availableValuesMap[attribute.id].has(val.id))
            .sort((a, b) => a.order - b.order);

          return { ...attribute, values: validValues };
        })
        .filter(Boolean);

      // unorderedProduct.attributes.forEach((category) => {
      //   initialSelectedValues[category.id] = category?.values?.[0]?.id;
      // });

      // Sort the 'values' array inside each attribute
      // unorderedProduct.attributes.forEach((attribute) => {
      //   if (Array.isArray(attribute.values)) {
      //     attribute.values = attribute.values.sort((a, b) => a.order - b.order);
      //   }
      // });

      // const initialSelectedValues = {};

      // unorderedProduct.attributes.forEach((category) => {
      //   initialSelectedValues[category.id] = category?.values?.[0]?.id;
      // });

      const findSelectedVariant = (variants, attributes) => {
        return variants.find((variant) => {
          return attributes.every((attribute) => {
            const matchingVariantAttribute = variant.attributes.find(
              (variantAttribute) =>
                variantAttribute.product_attribute === attribute.id &&
                attribute.values.some(
                  (value) => value.id === variantAttribute.value,
                ),
            );
            return Boolean(matchingVariantAttribute);
          });
        });
      };

      let defaultSelectedVariant = findSelectedVariant(
        unorderedProduct.variants,
        unorderedProduct.attributes,
      );

      if (!defaultSelectedVariant) {
        defaultSelectedVariant = unorderedProduct.variants[0];
      }

      const initialSelectedValues = {};
      unorderedProduct.attributes.forEach((category) => {
        const variantAttr = defaultSelectedVariant.attributes.find(
          (attr) => attr.product_attribute === category.id,
        );
        if (variantAttr) {
          initialSelectedValues[category.id] = variantAttr.value;
        } else {
          initialSelectedValues[category.id] = category?.values?.[0]?.id;
        }
      });

      setSameVariantProducts(unorderedProduct);
      setSelectedCategories(initialSelectedValues);
      setSelectedVariant(defaultSelectedVariant);
    }
  };

  useEffect(() => {
    getSameVariants(
      merchantConfig.merchant_id,
      selectedProduct.product_id,
      quantityInc,
      selectedProduct.product_price,
      selectedProduct.variant_id,
      selectedProduct.sku,
      selectedOrder.exchange_configurations.length > 0
        ? selectedOrder.exchange_configurations.includes("lower_value_product")
        : undefined,
      selectedOrder.exchange_configurations.length > 0
        ? selectedOrder.exchange_configurations.includes("higher_value_product")
        : undefined,
      rmsConfig.product_service_version,
    )
      .then((resp) => {
        setAPIVersion(resp.data.api_version);
        orderRequiredProduct(resp.data.products, resp.data.api_version);
        setSameVariantLoader(false);
      })
      .catch((err) => {
        console.log(err);
        setSameApiError(true);
      });
  }, []);

  useEffect(() => {
    if (isReplacement) {
      setSelectedVariant(buildVariantFromProduct(selectedProduct));
    }
  }, [isReplacement, selectedProduct]);

  const handleProductExchange = () => {
    if (customSizeError) return;
    const requestReturnType = isReplacement ? "replacement" : "exchange";
    const shipmentApplicable = isReplacement
      ? rmsConfig.shipment_applicable_for_replacements !== false
      : selectedProduct.shipment_applicable;

    dispatch(mobSelected(false));
    dispatch(updateWebSelected(false));
    dispatch(addNewProduct(selectedVariant));
    dispatch(returnQuantity(quantityInc));
    dispatch(returnType(requestReturnType));
    dispatch(addUploads([]));
    setUploads([]);
    let tempProducts = [...returnedProducts];
    let line_item_ids = tempProducts.map((a) => a.line_item_id);

    const exchangeVariant = isReplacement
      ? buildVariantFromProduct(selectedProduct)
      : selectedVariant;

    if (!exchangeVariant) {
      return;
    }

    const exchangeProductTitle =
      !isReplacement && sameVariantProducts?.title
        ? sameVariantProducts.title
        : selectedProduct.product_title;

    const exchangeProductPrice = isReplacement
      ? selectedProduct.product_price
      : exchangeVariant.price;

    const exchangeOriginalPrice = isReplacement
      ? selectedProduct.original_product_price
      : exchangeVariant.original_product_price;

    const exchangeVariantImage =
      (exchangeVariant.variant_img && exchangeVariant.variant_img.src) ||
      exchangeVariant.variant_img ||
      selectedProduct.image_url;

    const exchangeVariantName =
      exchangeVariant.title || selectedProduct.variant_name;

    if (selectedGroupProducts.length > 0) {
      for (let i = selectedGroupProducts.length - 1; i >= 0; i--) {
        const product = selectedGroupProducts[i];

        const index = line_item_ids.indexOf(product.line_item_id);

        if (index !== -1) {
          tempProducts.splice(index, 1);
          line_item_ids.splice(index, 1);
        }
      }
    } else {
      if (line_item_ids.indexOf(selectedProduct.line_item_id) !== -1) {
        let index = line_item_ids.indexOf(selectedProduct.line_item_id);
        tempProducts.splice(index, 1);
      }
    }

    let childProducts = selectedGroupProducts.filter(
      (groupProduct) =>
        groupProduct.is_child === true &&
        (isReplacement
          ? groupProduct.replacement === true
          : groupProduct.exchange_eligible === true) &&
        groupProduct.auto_add === true,
    );

    dispatch(
      addReturnedProducts([
        ...tempProducts,
        {
          merchant_id: merchantConfig.merchant_id,
          line_item_id: selectedProduct.line_item_id,

          product_id: selectedProduct.product_id,
          variant_id: selectedProduct.variant_id,
          product_price: selectedProduct.product_price,
          quantity: quantityInc,
          product_title: selectedProduct.product_title,
          variant_name: selectedProduct.variant_name,
          product_sku: selectedProduct.sku,
          original_product_price: selectedProduct.original_product_price,
          variant_weight: selectedProduct?.variant_weight || "",
          variant_weight_unit: selectedProduct?.variant_weight_unit || "",
          variant_image_url: selectedProduct.image_url,

          exchange_product_id: selectedProduct.product_id,
          exchange_variant_id: exchangeVariant.variant_id,
          exchange_product_sku: exchangeVariant.sku,
          exchange_product_price: exchangeProductPrice,
          exchange_product_title: exchangeProductTitle,
          exchange_variant_image_url: exchangeVariantImage,
          exchange_variant_name: exchangeVariantName,
          original_exchange_product_price: exchangeOriginalPrice,
          exchange_variant_weight: exchangeVariant?.variant_weight || "",
          exchange_variant_weight_unit:
            exchangeVariant?.variant_weight_unit || "",

          return_type: requestReturnType,
          refund_mode_offer_amount: 0, // Not sure why but we give based on line items as well

          customer_reason_code: reason.reason_code,
          customer_reason_label: reason.reason_label,
          customer_reason_comment: otherReason,
          customer_sub_reason_code:
            subReason && subReason !== null ? subReason.reason_code : null,
          customer_sub_reason_label:
            subReason && subReason !== null ? subReason.reason_label : null,

          total_item_discount: selectedProduct.total_item_discount,
          discounted_price: selectedProduct.discounted_price,

          exchange_applicable: selectedProduct.exchange_applicable,
          refund_applicable: selectedProduct.refund_applicable,
          shipment_applicable: shipmentApplicable,

          product_group_id:
            selectedProduct.product_group_id || customProductGroupId,
          is_parent: selectedProduct.is_parent,
          is_child: selectedProduct.is_child,
          properties: customProperties || selectedProduct.properties,

          // Not sure why, Just hard coded optional fields
          variant_length: 0,
          variant_breadth: 0,
          variant_height: 0,
          exchange_variant_length: 0,
          exchange_variant_breadth: 0,
          exchange_variant_height: 0,
          discount_applied: false,
          item_amount_difference: 0,
          total_item_amount_difference: 0,

          remaining_qty: selectedProduct.quantity - quantityInc,
          exchange_type: "same",
          applicable_refund_modes: null,
          ...getLineItemMetadata(selectedProduct),
        },
        ...childProducts.map((childProduct) => ({
          merchant_id: merchantConfig.merchant_id,
          line_item_id: childProduct.line_item_id,

          product_id: childProduct.product_id,
          variant_id: childProduct.variant_id,
          product_price: childProduct.product_price,
          quantity: quantityInc,
          product_title: childProduct.product_title,
          variant_name: childProduct.variant_name,
          product_sku: childProduct.sku,
          original_product_price: childProduct.original_product_price,
          variant_weight: childProduct?.variant_weight || "",
          variant_weight_unit: childProduct?.variant_weight_unit || "",
          variant_image_url: childProduct.image_url,

          exchange_product_id: childProduct.product_id,
          exchange_variant_id: childProduct.variant_id,
          exchange_product_sku: childProduct.sku,
          exchange_product_price: childProduct.product_price,
          exchange_product_title: childProduct.product_title,
          exchange_variant_image_url: childProduct.image_url,
          exchange_variant_name: childProduct.variant_name,
          original_exchange_product_price: childProduct.original_product_price,
          exchange_variant_weight: childProduct?.variant_weight || "",
          exchange_variant_weight_unit: childProduct?.variant_weight_unit || "",

          return_type: requestReturnType,
          refund_mode_offer_amount: 0, // Not sure why but we give based on line items as well

          customer_reason_code: reason.reason_code,
          customer_reason_label: reason.reason_label,
          customer_reason_comment: otherReason,
          customer_sub_reason_code:
            subReason && subReason !== null ? subReason.reason_code : null,
          customer_sub_reason_label:
            subReason && subReason !== null ? subReason.reason_label : null,

          total_item_discount: childProduct.total_item_discount,
          discounted_price: childProduct.discounted_price,

          exchange_applicable: childProduct.exchange_applicable,
          refund_applicable: childProduct.refund_applicable,
          shipment_applicable: isReplacement
            ? rmsConfig.shipment_applicable_for_replacements !== false
            : childProduct.shipment_applicable,

          product_group_id: childProduct.product_group_id,
          is_parent: childProduct.is_parent,
          is_child: childProduct.is_child,
          properties: childProduct.properties,

          // Not sure why, Just hard coded optional fields
          variant_length: 0,
          variant_breadth: 0,
          variant_height: 0,
          exchange_variant_length: 0,
          exchange_variant_breadth: 0,
          exchange_variant_height: 0,
          discount_applied: false,
          item_amount_difference: 0,
          total_item_amount_difference: 0,

          remaining_qty: childProduct.quantity - quantityInc,
          exchange_type: "same",
          applicable_refund_modes: null,
          ...getLineItemMetadata(childProduct),
        })),
      ]),
    );

    dispatch(setReplacementMode(false));
    dispatch(addProceedBool(false));
    setAnimate(true);
    // Reset animation after it's done if needed
    setTimeout(() => {
      setAnimate(false);
    }, 700); // Duration matches the animation
  };

  const handleExchangeSelectionClick = () => {
    if (isSameVariantSelected) {
      setSameVariantConfirmationOpen(true);
      return;
    }

    handleProductExchange();
  };

  useEffect(() => {
    if (!isReplacement) {
      hasAutoProcessedRef.current = false;
      return;
    }

    if (selectedVariant && !hasAutoProcessedRef.current) {
      hasAutoProcessedRef.current = true;
      handleProductExchange();
    }
  }, [handleProductExchange, isReplacement, selectedVariant]);

  useEffect(() => {
    if (isReplacement) {
      return;
    }

    if (APIVersion === "v1") {
      if (
        sameVariantProducts &&
        sameVariantProducts !== null &&
        Object.keys(sameVariantProducts).length > 0 &&
        sameVariantProducts.variants.length > 0
      ) {
        let variantToSelect = null;

        if (selectedFilterVariantId) {
          variantToSelect = sameVariantProducts.variants.find(
            (v) => v.variant_id === selectedFilterVariantId,
          );
        }

        if (!variantToSelect) {
          const filteredVariants = sameVariantProducts.variants.filter((a) =>
            !reason.same_variant_exchange
              ? a.variant_id != selectedProduct.variant_id
              : true,
          );
          variantToSelect =
            filteredVariants[0] || sameVariantProducts.variants[0];
        }

        setSelectedVariant(variantToSelect);
        setSelectedFilterVariantId(null);
      } else {
        setSelectedVariant(null);
      }
    }
  }, [sameVariantProducts, isReplacement]);

  useEffect(() => {
    if (
      selectedVariant &&
      selectedVariant?.errors &&
      selectedVariant.errors.length > 0
    ) {
      document?.querySelector(".out-of-stock")?.scrollIntoView();
    }
  }, [selectedVariant]);

  const getProductDetails = (product) => {
    if (APIVersion === "v1") {
      setSelectedVariant(product);
      setSameVariantLoader(false);
    } else {
      getSameVariants(
        merchantConfig.merchant_id,
        product.product_id,
        quantityInc,
        selectedProduct.product_price,
        selectedProduct.variant_id,
        selectedProduct.sku,
        selectedOrder.exchange_configurations.length > 0
          ? selectedOrder.exchange_configurations.includes(
              "lower_value_product",
            )
          : undefined,
        selectedOrder.exchange_configurations.length > 0
          ? selectedOrder.exchange_configurations.includes(
              "higher_value_product",
            )
          : undefined,
        rmsConfig.product_service_version,
      )
        .then((resp) => {
          orderRequiredProduct(resp.data.products, resp.data.api_version);
          setSameVariantLoader(false);
        })
        .catch(() => {
          setSameApiError(true);
        });
    }
  };

  const handleVariant = (event) => {
    const variant = event.target.value;
    setSelectedFilterVariantId(variant.variant_id);
    setSelectedVariant(variant);
    openSameVariantConfirmationIfNeeded(variant);
  };

  const handleSelectCategory = (categoryId, value) => {
    // Update selected categories
    const updatedSelectedCategories = {
      ...selectedCategories,
      [categoryId]: value,
    };

    // Sort the selected categories by the category order (position) in `categories`
    const sortedSelectedCategories = Object.keys(updatedSelectedCategories)
      .sort((a, b) => {
        const positionA = sameVariantProducts.attributes.find(
          (cat) => cat.id === a,
        )?.position;
        const positionB = sameVariantProducts.attributes.find(
          (cat) => cat.id === b,
        )?.position;
        return positionA - positionB;
      })
      .reduce((acc, key) => {
        acc[key] = updatedSelectedCategories[key];
        return acc;
      }, {});

    const findSelectedVariantWithMapping = (variants, selectedAttributes) => {
      return variants.find((variant) => {
        return Object.entries(selectedAttributes).every(
          ([productAttribute, value]) => {
            return variant.attributes.some(
              (variantAttribute) =>
                variantAttribute.product_attribute ===
                  parseInt(productAttribute, 10) &&
                variantAttribute.value === value,
            );
          },
        );
      });
    };

    const matchedVariant = findSelectedVariantWithMapping(
      sameVariantProducts.variants,
      sortedSelectedCategories,
    );

    // Set the sorted and normalized categories
    setSelectedCategories(sortedSelectedCategories);

    if (matchedVariant) {
      setSelectedVariant(matchedVariant);
      openSameVariantConfirmationIfNeeded(matchedVariant);
    }
  };

  const CategoryDisplay = ({ category }) => {
    return (
      <div style={{ margin: "10px 0px" }}>
        <p style={{ color: "#111111", fontSize: "14px" }}>
          {category.display_name}
        </p>
        <div style={{ display: "flex", flexWrap: "wrap" }}>
          {category?.values?.map((attr) => {
            return (
              <div key={attr.id} style={{ position: "relative" }}>
                {/* Hidden radio button */}
                <input
                  type="radio"
                  id={`${category.id}-${attr.id}`}
                  name={category.name}
                  value={attr.value}
                  checked={selectedCategories[category.id] === attr.id}
                  onChange={() => handleSelectCategory(category.id, attr.id)}
                  style={{ display: "none" }} // Hide the radio button
                />
                <p
                  onClick={() => handleSelectCategory(category.id, attr.id)}
                  style={{
                    border: `1px solid ${merchantConfig.primary_theme_color}`,
                    backgroundColor:
                      selectedCategories[category.id] === attr.id
                        ? merchantConfig.primary_theme_color
                        : "transparent",
                    color:
                      selectedCategories[category.id] === attr.id
                        ? "#fff"
                        : merchantConfig.primary_theme_color,
                    borderRadius: merchantConfig.use_rounded_edge_buttons
                      ? "4px"
                      : "0px",
                    padding: "0px 8px",
                    margin: "5px 8px 5px 0px",
                    minWidth: "30px",
                    minHeight: "30px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                    cursor: "pointer",
                  }}
                >
                  {attr.display_value}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        {sameApiError ? (
          <LoadingError marginTop="100px" padding="0px 20px" />
        ) : sameVariantLoader ? (
          [...Array(3).keys()].map((a) => {
            return (
              <div key={a} style={{ padding: "0px 20px" }}>
                <Skeleton
                  style={{
                    width: "140px",
                    height: "150px",
                    margin: "0px 5px 6px",
                  }}
                />
                <Typography
                  style={{
                    color: "#3B444DB2",
                    margin: "5px 0px",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  {" "}
                  <Skeleton />{" "}
                </Typography>
                <Typography
                  style={{
                    color: "#3B444DB2",
                    margin: "5px 0px",
                    fontSize: "14px",
                  }}
                >
                  {" "}
                  <Skeleton />
                </Typography>
                <Typography
                  style={{
                    color: "#3B444D",
                    fontSize: "16px",
                    margin: "5px 0px",
                    fontWeight: "600",
                  }}
                >
                  {" "}
                  <Skeleton />
                </Typography>
              </div>
            );
          })
        ) : sameVariantProducts.variants.length > 0 &&
          selectedVariant !== null ? (
          <div style={{ margin: "10px 0px", width: "100%" }}>
            <StyledDifferentProduct>
              <div className="image-container">
                <LazyLoadImage
                  effect="opacity"
                  src={
                    (selectedVariant !== null &&
                      selectedVariant?.variant_img?.src) ||
                    selectedVariant?.variant_img ||
                    selectedVariant?.src
                  }
                  alt={
                    selectedVariant?.variant_img?.alt ||
                    sameVariantProducts.title
                  }
                  className="product-image"
                />
              </div>
              <div className="product-details">
                {sameVariantProducts.title &&
                  sameVariantProducts.title !== "" && (
                    <p
                      className="product-title"
                      style={{ color: merchantConfig.primary_theme_color }}
                    >
                      {sameVariantProducts.title}
                    </p>
                  )}
                <p
                  style={{
                    fontSize: "16px",
                  }}
                >
                  {selectedVariant.title.indexOf("Default") === -1
                    ? selectedVariant.title
                    : sameVariantProducts.title}
                </p>
                {merchantConfig.merchant_id === 977 && (
                  <div>
                    <p
                      style={{
                        color: "#3B444D",
                        fontSize: "16px",
                        margin: "5px 0px",
                        fontWeight: "700",
                      }}
                    >
                      {rmsConfig.discount_config &&
                        Object.keys(rmsConfig.discount_config).length > 0 &&
                        rmsConfig.discount_config.target_type ===
                          "line_item" && (
                          <span>
                            ₹{" "}
                            {rmsConfig.discount_config.type === "fixed"
                              ? selectedVariant.price -
                                rmsConfig.discount_config.value
                              : (
                                  selectedVariant.price -
                                  (selectedVariant.price *
                                    rmsConfig.discount_config.value) /
                                    100
                                ).toFixed(2)}
                          </span>
                        )}
                      <span
                        style={{
                          textDecoration:
                            rmsConfig.discount_config &&
                            Object.keys(rmsConfig.discount_config).length > 0
                              ? "line-through"
                              : "none",
                          marginLeft:
                            rmsConfig.discount_config &&
                            Object.keys(rmsConfig.discount_config).length > 0
                              ? "10px"
                              : "0px",
                          color:
                            rmsConfig.discount_config &&
                            Object.keys(rmsConfig.discount_config).length > 0
                              ? "#3B444D66"
                              : "#3B444D",
                        }}
                      >
                        ₹ {selectedVariant.price}
                      </span>
                    </p>
                    <div>
                      {sameVariantProducts.attributes.length > 0 &&
                        sameVariantProducts.attributes
                          .filter((category) => category.name !== "size")
                          .map((category) => {
                            return <CategoryDisplay category={category} />;
                          })}
                      {sameVariantProducts.attributes.length > 0 &&
                        sameVariantProducts.attributes
                          .filter((category) => category.name === "size")
                          .map((category) => {
                            const groupByType = (items) => {
                              return items.reduce((acc, item) => {
                                const types = [
                                  "single",
                                  "double",
                                  "queen",
                                  "king",
                                ];
                                const type = types.find((t) =>
                                  item.value.includes(t),
                                );
                                if (!type) return acc;

                                const cleanedDisplayValue = item.display_value
                                  // Remove height (last dimension) using regex
                                  // .replace(/\s*x\s*\d+\s*inch/, "")
                                  // Remove type label in brackets (case-insensitive)
                                  .replace(/\s*\(\w+\)\s*$/, "")
                                  .trim();

                                const formattedItem = {
                                  ...item,
                                  group: type,
                                  display_value: cleanedDisplayValue,
                                };

                                acc[type] = acc[type] || [];
                                acc[type].push(formattedItem);
                                return acc;
                              }, {});
                            };

                            // Usage
                            const grouped = groupByType(category.values);

                            if (Object.keys(grouped).length > 0) {
                              return (
                                <SizeSelector
                                  grouped={grouped}
                                  sizeAttributeId={category.id}
                                  onCustomSizeError={setCustomSizeError}
                                  // relatedProducts={
                                  //   sameVariantProducts.related_products
                                  // }
                                  // title={
                                  //   sameVariantProducts?.secondary_display_title ||
                                  //   sameVariantProducts?.product_title ||
                                  //   sameVariantProducts.title
                                  // }
                                  // getProductDetails={getProductDetails}
                                  // setVariantsLoader={setSameVariantLoader}
                                  variants={sameVariantProducts.variants}
                                  setSelectedVariant={setSelectedVariant}
                                  selectedVariant={selectedVariant}
                                  setCustomProperties={setCustomProperties}
                                  selectedProduct={selectedProduct}
                                  setCustomProductGroupId={
                                    setCustomProductGroupId
                                  }
                                />
                              );
                            } else {
                              return <CategoryDisplay category={category} />;
                            }
                          })}
                    </div>
                  </div>
                )}

                {sameVariantProducts.variants.length >= 10 &&
                  APIVersion === "v1" && (
                    <TextField
                      style={{
                        width: "100%",
                        margin: "15px 0px",
                        maxWidth: "280px",
                      }}
                      select
                      variant="outlined"
                      value={selectedVariant}
                      onChange={handleVariant}
                    >
                      {sameVariantProducts.variants
                        .filter((a) => {
                          return !reason.same_variant_exchange
                            ? a.variant_id != selectedProduct.variant_id
                            : true;
                        })
                        .map((variant) => {
                          return (
                            <MenuItem value={variant} key={variant.id}>
                              {variant.title}
                            </MenuItem>
                          );
                        })}
                    </TextField>
                  )}
                {APIVersion === "v2" &&
                  merchantConfig.merchant_id !== 977 &&
                  sameVariantProducts?.attributes.length > 0 &&
                  sameVariantProducts?.attributes.map((category) => {
                    return (
                      <div style={{ margin: "10px 0px" }}>
                        <p style={{ color: "#111111", fontSize: "14px" }}>
                          {category.display_name}
                        </p>
                        <div style={{ display: "flex", flexWrap: "wrap" }}>
                          {category.values.map((attr) => {
                            return (
                              <div
                                key={attr.id}
                                style={{ position: "relative" }}
                              >
                                {/* Hidden radio button */}
                                <input
                                  type="radio"
                                  id={`${category.id}-${attr.id}`}
                                  name={category.name}
                                  value={attr.value}
                                  checked={
                                    selectedCategories[category.id] === attr.id
                                  }
                                  onChange={() =>
                                    handleSelectCategory(category.id, attr.id)
                                  }
                                  style={{ display: "none" }} // Hide the radio button
                                />
                                <p
                                  onClick={() =>
                                    handleSelectCategory(category.id, attr.id)
                                  }
                                  style={{
                                    border: `1px solid ${merchantConfig.primary_theme_color}`,
                                    backgroundColor:
                                      selectedCategories[category.id] ===
                                      attr.id
                                        ? merchantConfig.primary_theme_color
                                        : "transparent",
                                    color:
                                      selectedCategories[category.id] ===
                                      attr.id
                                        ? "#fff"
                                        : merchantConfig.primary_theme_color,
                                    borderRadius: "4px",
                                    padding: "0px 8px",
                                    margin: "5px 8px 5px 0px",
                                    minWidth: "30px",
                                    minHeight: "30px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "12px",
                                    cursor: "pointer",
                                  }}
                                >
                                  {attr.display_value}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                {selectedVariant &&
                  selectedVariant !== null &&
                  ((merchantConfig.merchant_id === 541 &&
                    selectedVariant.inventory_quantity < 7) ||
                    selectedVariant.out_of_stock ||
                    !selectedVariant.quantity_available_for_exchange) && (
                    <div
                      className="out-of-stock"
                      style={{
                        color: "#A02923",
                        background: "#FCF2F2",
                        border: "1px solid #F4C9C9",
                        borderRadius: merchantConfig.use_rounded_edge_buttons
                          ? "6px"
                          : "0px",
                        padding: "1rem",
                        fontSize: "12px",
                        display: "flex",
                      }}
                    >
                      <ErrorOutlineRoundedIcon
                        style={{ fontSize: "18px", color: "#E63E36" }}
                      />
                      <div style={{ marginLeft: "6px", width: "100%" }}>
                        <p
                          style={{
                            fontWeight: "600",
                            fontSize: "14px",
                          }}
                        >
                          Product not available for exchange
                        </p>
                        {selectedVariant?.errors &&
                        selectedVariant.errors.length > 0
                          ? selectedVariant.errors.map((error) => {
                              return (
                                <p key={error.error_code}>
                                  - {error.error_code_message}
                                </p>
                              );
                            })
                          : "Out of stock"}
                      </div>
                    </div>
                  )}
                {isSameVariantSelected && (
                    <div
                      style={{
                        background: "#FFFBEB",
                        border: "1px solid #FEE686",
                        padding: "1rem",
                        color: "#963D1F",
                        display: "flex",
                        borderRadius: merchantConfig.use_rounded_edge_buttons
                          ? "6px"
                          : "0px",
                      }}
                    >
                      <WarningAmberRoundedIcon
                        style={{ fontSize: "18px", color: "#E17433" }}
                      />
                      <div style={{ marginLeft: "6px" }}>
                        <p
                          style={{
                            fontWeight: "600",
                            fontSize: "14px",
                          }}
                        >
                          Same variant selected for exchange
                        </p>
                        <p style={{ fontSize: "12px" }}>
                          You have selected the same variant{" "}
                          {selectedProduct.variant_name} that you originally
                          purchased.
                        </p>
                      </div>
                    </div>
                  )}
              </div>
            </StyledDifferentProduct>
            {sameVariantProducts.variants.length < 10 &&
              sameVariantProducts.variants.length > 1 &&
              APIVersion === "v1" && (
                <StyledVariantSelect>
                  <p
                    style={{
                      margin: "10px 0px",
                      fontSize: "14px",
                      fontWeight: "700",
                    }}
                  >
                    Select Variant
                  </p>
                  <div style={{ display: "flex", flexWrap: "wrap" }}>
                    {sameVariantProducts.variants
                      .filter((a) => {
                        return !reason.same_variant_exchange
                          ? a.variant_id != selectedProduct.variant_id
                          : true;
                      })
                      .map((variant) => {
                        return (
                          <Button
                            disableRipple
                            variant={
                              selectedVariant !== null &&
                              selectedVariant.variant_id === variant.variant_id
                                ? "contained"
                                : "outlined"
                            }
	                            onClick={() => {
	                              setSelectedFilterVariantId(variant.variant_id);
	                              setSelectedVariant(variant);
	                              openSameVariantConfirmationIfNeeded(variant);
	                            }}
                            style={{
                              fontSize: "12px",
                              minWidth: "50px",
                              width: "fit-content",
                              margin: "8px",
                              marginLeft: "0px",
                              background:
                                selectedVariant !== null &&
                                selectedVariant.variant_id ===
                                  variant.variant_id
                                  ? `${merchantConfig.primary_theme_color}1A`
                                  : "transparent",
                              color:
                                selectedVariant !== null &&
                                selectedVariant.variant_id ===
                                  variant.variant_id
                                  ? `#000`
                                  : `${merchantConfig.primary_theme_color}`,
                            }}
                            key={variant.id}
                          >
                            {variant.title}
                          </Button>
                        );
                      })}
                  </div>
                </StyledVariantSelect>
              )}

            <div
              style={{
                padding: "0 1rem",
                fontSize: "14px",
                marginTop: "10px",
                color: "#3B444D",
              }}
            >
              {parse(sameVariantProducts?.rich_description || "")}
            </div>
            <div className="diff-prod-buttons">
              <div
                style={{
                  display: "flex",
                  position: "absolute",
                  width: "100%",
                  maxWidth: "600px",
                  bottom: 0,
                }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  className="back-button"
                  onClick={() => {
                    dispatch(addProceedBool(false));
                    dispatch(addProductType(null));
                  }}
                  style={{
                    borderRadius: merchantConfig.use_rounded_edge_buttons
                      ? "0px 0px 0px 10px"
                      : "0px",
                    background: "#fff",
                  }}
                >
                  Back
                </Button>
                <Button
                  className="exchange-button-web"
                  color="primary"
	                  onClick={handleExchangeSelectionClick}
                  variant="contained"
                  endIcon={
                    <ArrowForwardIcon
                      sx={{ position: "relative", top: "-2px" }}
                    />
                  }
	                  disabled={customSizeError || isSelectedVariantUnavailable}
                  style={{
                    borderRadius: merchantConfig.use_rounded_edge_buttons
                      ? "0px 0px 10px 0px"
                      : "0px",
                  }}
                >
                  Select for exchange
                </Button>
              </div>
              <div className="exchange-button-mob">
                <Button
                  color="primary"
                  fullWidth
	                  onClick={handleExchangeSelectionClick}
                  variant="contained"
                  endIcon={
                    <ArrowForwardIcon
                      sx={{ position: "relative", top: "-2px" }}
                    />
                  }
                  style={{
                    transform: "translateX(-50%)",
                    left: "50%",
                    padding: "12px 0px",
                    borderRadius: merchantConfig.use_rounded_edge_buttons
                      ? "0px 0px 10px 10px"
                      : "0px",
                  }}
	                  disabled={customSizeError || isSelectedVariantUnavailable}
                >
                  Select for exchange
                </Button>
	              </div>
	            </div>

	            <Modal
	              aria-labelledby="same-variant-confirmation-title"
	              aria-describedby="same-variant-confirmation-description"
	              open={sameVariantConfirmationOpen}
	              onClose={() => setSameVariantConfirmationOpen(false)}
	              closeAfterTransition
	            >
	              <div
	                style={{
	                  position: "absolute",
	                  top: "50%",
	                  left: "50%",
	                  transform: "translate(-50%, -50%)",
	                  minWidth: "300px",
	                  padding: "20px 24px 24px",
	                  background: "#fff",
	                  maxWidth: "460px",
	                  width: "90%",
	                  borderRadius: merchantConfig.use_rounded_edge_buttons
	                    ? "10px"
	                    : "0px",
	                }}
	              >
	                <div
	                  style={{
	                    display: "flex",
	                    justifyContent: "space-between",
	                    alignItems: "flex-start",
	                    gap: "16px",
	                  }}
	                >
	                  <div>
	                    <p
	                      id="same-variant-confirmation-title"
	                      style={{
	                        fontWeight: "600",
	                        fontSize: "16px",
	                        marginBottom: "8px",
	                      }}
	                    >
	                      Same size selected
	                    </p>
	                    <p
	                      id="same-variant-confirmation-description"
	                      style={{ color: "#3B444D", fontSize: "14px" }}
	                    >
	                      You selected the same size{" "}
	                      {selectedProduct?.variant_name || ""} that you
	                      originally purchased. Do you want to continue with this
	                      exchange?
	                    </p>
	                  </div>
	                  <CloseIcon
	                    style={{
	                      width: "20px",
	                      height: "20px",
	                      stroke: "#3B444D",
	                      cursor: "pointer",
	                      flexShrink: 0,
	                    }}
	                    onClick={() => setSameVariantConfirmationOpen(false)}
	                  />
	                </div>
	                <div
	                  style={{
	                    display: "flex",
	                    justifyContent: "flex-end",
	                    gap: "10px",
	                    marginTop: "24px",
	                  }}
	                >
	                  <Button
	                    variant="outlined"
	                    color="primary"
	                    onClick={() => setSameVariantConfirmationOpen(false)}
	                  >
	                    No
	                  </Button>
	                  <Button
	                    variant="contained"
	                    color="primary"
	                    onClick={() => {
	                      setSameVariantConfirmationOpen(false);
	                      handleProductExchange();
	                    }}
	                  >
	                    Yes, continue
	                  </Button>
	                </div>
	              </div>
	            </Modal>
	          </div>
	        ) : (
          <div
            style={{
              justifyContent: "center",
              textAlign: "center",
              marginTop: "4rem",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Search />
            <p style={{ textAlign: "center", marginTop: "1.5rem" }}>
              Hmm we don't have that..yet
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default SameProduct;
